import apiClient from './apiClient'
import { cleanMediaUrl } from '@/utils/text'

/** Primer mensaje útil de una respuesta DRF (detail, error, o primer campo). */
function drfErrorMessage(data, fallback) {
  if (!data || typeof data !== 'object') return fallback
  if (typeof data.detail === 'string') return data.detail
  if (data.error) return String(data.error)
  for (const key of Object.keys(data)) {
    const v = data[key]
    if (Array.isArray(v) && v.length) return String(v[0])
    if (typeof v === 'string') return v
  }
  return fallback
}

const normalizeCarouselImage = (image) => {
  if (!image || typeof image !== 'object') return image
  return {
    ...image,
    image: cleanMediaUrl(image.image),
    mobileImage: cleanMediaUrl(image.mobileImage),
    desktop_image: cleanMediaUrl(image.desktop_image),
    mobile_image: cleanMediaUrl(image.mobile_image),
  }
}

export const uploadFile = async (file, options = {}) => {
  const formData = new FormData()
  formData.append('file', file)
  const subdir = options.subdirectory || options.destination || 'blog'
  formData.append('subdirectory', subdir)
  if (!options.subdirectory && options.destination) {
    formData.append('destination', options.destination)
  }

  try {
    const response = await apiClient.post('/uploads/', formData, {
      timeout: 60000,
    })
    const data = response.data || {}
    const url = cleanMediaUrl(data.url || data.public_url || data.secure_url)
    return { ...data, url, public_url: url }
  } catch (error) {
    throw new Error(
      drfErrorMessage(error.response?.data, 'Error al subir el archivo')
    )
  }
}

export const getPublicCarouselImages = async () => {
  try {
    const response = await apiClient.get('/uploads/carousel/public_carousel/')
    return Array.isArray(response.data)
      ? response.data.map(normalizeCarouselImage)
      : response.data
  } catch (error) {
    console.error('Error fetching carousel images:', error)
    throw new Error(error.response?.data?.message || 'Error al obtener las imágenes del carousel')
  }
}

export const getCarouselImages = async () => {
  try {
    const response = await apiClient.get('/uploads/carousel/')
    return Array.isArray(response.data)
      ? response.data.map(normalizeCarouselImage)
      : response.data
  } catch (error) {
    console.error('Error fetching carousel images:', error)
    throw new Error(error.response?.data?.message || 'Error al obtener las imágenes del carousel')
  }
}

export const updateCarouselImageStatus = async (id, isActive) => {
  try {
    const response = await apiClient.patch(
      `/uploads/carousel/${id}/`,
      { is_active: isActive },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`
        }
      }
    )
    return response.data
  } catch (error) {
    console.error('Error updating carousel image status:', error)
    throw new Error(error.response?.data?.message || 'Error al actualizar el estado de la imagen')
  }
}

export const updateCarouselImageOrder = async (imageId1, newOrder1, imageId2, newOrder2) => {
  try {
    const [response1, response2] = await Promise.all([
      apiClient.patch(
        `/uploads/carousel/${imageId1}/`,
        { order: newOrder1 },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      ),
      apiClient.patch(
        `/uploads/carousel/${imageId2}/`,
        { order: newOrder2 },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      )
    ])

    return { image1: response1.data, image2: response2.data }
  } catch (error) {
    console.error('Error updating carousel image order:', error)
    throw new Error(error.response?.data?.message || 'Error al actualizar el orden de las imágenes')
  }
}

const CAROUSEL_UPLOAD_SUBDIR = 'carousel'

export const uploadCarouselImage = async (imageData) => {
  if (!imageData.desktopImage) {
    throw new Error('Falta la imagen principal')
  }
  const desktopRes = await uploadFile(imageData.desktopImage, {
    subdirectory: CAROUSEL_UPLOAD_SUBDIR
  })
  const body = {
    desktop_image: desktopRes.url,
    link: imageData.link || '/productos',
    order: imageData.order ?? 0,
    is_active:
      imageData.isActive !== undefined && imageData.isActive !== null
        ? !!imageData.isActive
        : true
  }
  if (imageData.mobileImage) {
    const mobileRes = await uploadFile(imageData.mobileImage, {
      subdirectory: CAROUSEL_UPLOAD_SUBDIR
    })
    body.mobile_image = mobileRes.url
  }

  try {
    const response = await apiClient.post('/uploads/carousel/', body)
    return response.data
  } catch (error) {
    console.error('Error uploading carousel image:', error)
    throw new Error(
      drfErrorMessage(error.response?.data, 'Error al subir la imagen del carousel')
    )
  }
}

export const updateCarouselImage = async (id, imageData) => {
  const body = {}
  if (imageData.link) body.link = imageData.link
  if (imageData.order !== undefined) body.order = imageData.order
  if (imageData.isActive !== undefined && imageData.isActive !== null) {
    body.is_active = !!imageData.isActive
  }
  if (imageData.desktopImage) {
    const res = await uploadFile(imageData.desktopImage, {
      subdirectory: CAROUSEL_UPLOAD_SUBDIR
    })
    body.desktop_image = res.url
  }
  if (imageData.mobileImage) {
    const res = await uploadFile(imageData.mobileImage, {
      subdirectory: CAROUSEL_UPLOAD_SUBDIR
    })
    body.mobile_image = res.url
  }

  if (Object.keys(body).length === 0) {
    throw new Error('No hay cambios para guardar')
  }

  try {
    const response = await apiClient.patch(`/uploads/carousel/${id}/`, body)
    return response.data
  } catch (error) {
    console.error('Error updating carousel image:', error)
    throw new Error(
      drfErrorMessage(error.response?.data, 'Error al actualizar la imagen')
    )
  }
}

export const deleteCarouselImage = async (id) => {
  try {
    const response = await apiClient.delete(
      `/uploads/carousel/${id}/`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`
        }
      }
    )
    return response.data
  } catch (error) {
    console.error('Error deleting carousel image:', error)
    throw new Error(error.response?.data?.detail || 'Error al eliminar la imagen')
  }
}
